import {
  FunctionComponent,
  forwardRef,
  Ref,
  PropsWithoutRef,
  KeyboardEvent,
  useRef,
  useCallback
} from 'react';
import styled, { css } from 'styled-components';

import { ForwardProps } from '../../../types';
import { defaultThemeProp } from '../../../theme';
import Selectable, { StyledSelectable } from '../../Badges/Selection';
import { StyledFormControl } from '../../FormControl';
import Flex from '../../Flex';
import { useArrows, useConsolidatedRef, useDirection } from '../../../hooks';
import { cap } from '../../../utils';
import { StyledSelectInput } from '../ComboBox.styles';

import { MultiSelectInputProps } from './MultiSelectInput.types';

export const StyledMultiSelectInput = styled(StyledSelectInput)(({ theme }) => {
  return css`
    ul {
      list-style: none;
    }

    ${StyledSelectable} {
      margin: calc(0.25 * ${theme.base.spacing});
    }
  `;
});

StyledMultiSelectInput.defaultProps = defaultThemeProp;

const MultiSelectInput: FunctionComponent<MultiSelectInputProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MultiSelectInputProps>, ref: Ref<HTMLInputElement>) => {
    const {
      selected = [],
      value,
      onRemove,
      onChange,
      onKeyDown,
      actions,
      readOnly,
      disabled,
      status,
      placeholder,
      autoFocus,
      ...restProps
    } = props;

    const inputRef = useConsolidatedRef<HTMLInputElement>(ref);
    const listRef = useRef<HTMLUListElement>(null);
    useArrows(listRef, { selector: 'div[role="button"], input', cycle: false, dir: 'left-right' });

    const onInputKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        const lastIndex = selected.length - 1;
        if (e.key === 'Backspace' && !value && selected.length > 0) {
          onRemove?.(selected[lastIndex].id, lastIndex);
        }
        onKeyDown?.(e);
      },
      [onKeyDown, value, selected]
    );

    const { end } = useDirection();

    return (
      <Flex
        as={StyledMultiSelectInput}
        forwardedAs={StyledFormControl}
        container={{ alignItems: 'center' }}
        {...{
          readOnly,
          disabled,
          status
        }}
      >
        <Flex
          ref={listRef}
          as='ul'
          item={{ grow: 1 }}
          container={{ alignItems: 'center', wrap: 'wrap' }}
          onBlur={() => {
            listRef.current?.querySelectorAll<HTMLElement>('div[role="button"]').forEach(el => {
              el.tabIndex = -1;
            });
            if (inputRef.current) inputRef.current.tabIndex = 0;
          }}
        >
          {selected.map((element, idx) => {
            return (
              <li key={element.id}>
                <Selectable
                  id={element.id.toString()}
                  disabled={readOnly || disabled}
                  status={status}
                  onRemove={id => {
                    onRemove?.(id, idx);
                    inputRef.current?.focus();
                  }}
                  onKeyDown={(e: KeyboardEvent<Element>) => {
                    // prevent browser's Back on FF
                    if (e.key === 'Backspace') e.preventDefault();
                    if (idx === selected.length - 1 && e.key === `Arrow${cap(end)}`)
                      inputRef.current?.focus();
                  }}
                >
                  {element.text}
                </Selectable>
              </li>
            );
          })}
          <Flex as='li' item={{ grow: 1 }} container>
            <input
              size={5}
              ref={inputRef}
              value={value}
              // no-op avoids react uncontrolled warning
              onChange={onChange || (() => {})}
              onKeyDown={onInputKeyDown}
              readOnly={readOnly || !onChange}
              placeholder={selected?.length ? undefined : placeholder}
              autoComplete='off'
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              {...restProps}
            />
          </Flex>
        </Flex>
        {actions}
      </Flex>
    );
  }
);

export default MultiSelectInput;
